/**
 * BranchingStory — interactive scrollytelling article explaining
 * how Thread's git-style versioning works for aerodynamic data.
 *
 * Uses Intersection Observer for scroll-triggered animations.
 * Professional git-graph diagrams with SVG glow effects, bezier
 * curves, and animated commit nodes (GitKraken / Cursor-inspired).
 */

import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Step {
  id: string;
  title: string;
  prose: string;
  code: string;
  output?: string;
  diagram?: "branch-create" | "commit" | "diff" | "merge" | "conflict-resolve" | "tags" | "squash" | "nested" | "full-workflow";
}

// ---------------------------------------------------------------------------
// Color palette — vibrant neon-on-dark, matches Radix dark theme
// ---------------------------------------------------------------------------

const C = {
  main:    "#3b82f6",   // blue
  feature: "#f97316",   // orange
  fine:    "#22c55e",   // green
  coarse:  "#a855f7",   // violet
  red:     "#ef4444",
  cyan:    "#06b6d4",
  yellow:  "#eab308",
  amber:   "#f59e0b",
  pink:    "#ec4899",
  bg:      "#0c0c0f",
  grid:    "#1a1a2e",
  text:    "#94a3b8",
  textBr:  "#e2e8f0",
};

// ---------------------------------------------------------------------------
// SVG building blocks
// ---------------------------------------------------------------------------

/** Shared SVG defs: glow filters, gradients */
function SvgDefs() {
  return (
    <defs>
      {/* Glow filters for each colour */}
      {[
        ["glow-blue", C.main],
        ["glow-orange", C.feature],
        ["glow-green", C.fine],
        ["glow-violet", C.coarse],
        ["glow-red", C.red],
        ["glow-cyan", C.cyan],
        ["glow-yellow", C.yellow],
        ["glow-pink", C.pink],
      ].map(([id, color]) => (
        <filter key={id} id={id} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
          <feFlood floodColor={color} floodOpacity="0.6" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      ))}
      {/* Soft drop shadow for labels */}
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
        <feOffset dy="2" />
        <feComposite in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

/** A commit node with outer ring + inner fill + optional pulse */
function CommitNode({
  cx, cy, color, r = 7, glow, pulse, label, labelSide = "below",
}: {
  cx: number; cy: number; color: string; r?: number;
  glow?: string; pulse?: boolean; label?: string; labelSide?: "below" | "above" | "right";
}) {
  const filterAttr = glow ? `url(#${glow})` : undefined;
  const ly = labelSide === "above" ? cy - r - 10 : labelSide === "right" ? cy + 4 : cy + r + 16;
  const lx = labelSide === "right" ? cx + r + 8 : cx;
  const anchor = labelSide === "right" ? "start" : "middle";
  return (
    <g>
      {pulse && (
        <circle cx={cx} cy={cy} r={r + 6} fill="none" stroke={color} strokeWidth="1.5" opacity="0.4">
          <animate attributeName="r" from={String(r + 2)} to={String(r + 14)} dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.5" to="0" dur="2s" repeatCount="indefinite" />
        </circle>
      )}
      <circle cx={cx} cy={cy} r={r + 2} fill="none" stroke={color} strokeWidth="1.5" opacity="0.35" filter={filterAttr} />
      <circle cx={cx} cy={cy} r={r} fill={color} filter={filterAttr} />
      <circle cx={cx} cy={cy} r={r * 0.4} fill="white" opacity="0.9" />
      {label && (
        <text x={lx} y={ly} textAnchor={anchor} fill={C.text} fontSize="10"
          fontFamily="'SF Mono', Menlo, Consolas, monospace" fontWeight="500">
          {label}
        </text>
      )}
    </g>
  );
}

/** Branch label pill */
function BranchLabel({ x, y, text, color }: { x: number; y: number; text: string; color: string }) {
  const w = text.length * 7.5 + 16;
  return (
    <g filter="url(#shadow)">
      <rect x={x - w / 2} y={y - 10} width={w} height={20} rx={10} fill={color} opacity="0.15" stroke={color} strokeWidth="1" />
      <text x={x} y={y + 4} textAnchor="middle" fill={color} fontSize="11"
        fontFamily="'SF Mono', Menlo, Consolas, monospace" fontWeight="600">
        {text}
      </text>
    </g>
  );
}

/** Animated path that "draws" itself based on progress 0→1 */
function AnimatedPath({
  d, color, width = 2.5, glow, progress = 1, dashed,
}: {
  d: string; color: string; width?: number; glow?: string;
  progress?: number; dashed?: boolean;
}) {
  const ref = useRef<SVGPathElement>(null);
  const [len, setLen] = useState(1000);

  useEffect(() => {
    if (ref.current) setLen(ref.current.getTotalLength());
  }, [d]);

  const offset = len * (1 - Math.min(1, Math.max(0, progress)));

  return (
    <path
      ref={ref}
      d={d}
      fill="none"
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
      strokeDasharray={dashed ? "6 4" : String(len)}
      strokeDashoffset={dashed ? undefined : offset}
      filter={glow ? `url(#${glow})` : undefined}
      style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
    />
  );
}

/** Faint grid background for diagrams */
function GridBg({ w, h }: { w: number; h: number }) {
  const lines = [];
  for (let x = 0; x <= w; x += 30) {
    lines.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={h} stroke={C.grid} strokeWidth="0.5" />);
  }
  for (let y = 0; y <= h; y += 30) {
    lines.push(<line key={`h${y}`} x1={0} y1={y} x2={w} y2={y} stroke={C.grid} strokeWidth="0.5" />);
  }
  return <g opacity="0.4">{lines}</g>;
}

// ---------------------------------------------------------------------------
// Story data
// ---------------------------------------------------------------------------

const STEPS: Step[] = [
  {
    id: "intro",
    title: "Why version your aero data?",
    prose: `Your wing design evolves through hundreds of simulations. Each CFD run
produces forces, moments, pressure fields — data that downstream analysis depends on.
When someone changes the reference area or reruns a mesh study, how do you know what
changed, when, and why?

Thread treats aerodynamic data like source code. Every change is tracked.
Every decision can be reviewed. Every mistake can be undone.`,
    code: `# Thread tracks your simulation data the same way
# git tracks your source code — but for physics.

import flexcomputethread as fct

# Zero-config: patches numpy, pandas, matplotlib
fct.auto_setup()

# Connect to your Thread project
fct.connect(project_id="cessna-172")`,
    output: `[thread] Connected to https://thread.dev-simulation.cloud
[thread] project=cessna-172, branch=main`,
  },
  {
    id: "branches",
    title: "Branches: isolated experiments",
    prose: `Just like git branches let developers work in isolation,
Thread branches let engineers experiment without affecting the
validated baseline.

When you create a branch, Thread uses **Copy-on-Write** — no data
is copied. Only changes you make on the branch are stored. This means
branching is instant, even for projects with millions of data points.`,
    code: `# Create a branch for your experiment
fct.checkout("mesh-refinement-study")

# Your changes are isolated from main
fct.set_reference("wing_span", 11.2, units="m",
                   reason="Testing extended wingspan")

# See what changed
fct.diff("main")`,
    output: `[thread] Created branch 'mesh-refinement-study' (from 'main')
[thread] Switched to branch 'mesh-refinement-study'
[thread] Set wing_span = 11.2 m (3 downstream nodes invalidated)
[thread] Diff: 1 modified, 3 stale`,
    diagram: "branch-create",
  },
  {
    id: "nested",
    title: "Nested branches: sub-experiments",
    prose: `Sometimes you need to branch off a branch. Maybe you're exploring
two mesh strategies within your refinement study. Thread supports
**nested branching** — branches can fork from any parent, not just main.

The Copy-on-Write resolution walks the entire parent chain. A node on
\`mesh-study/fine\` overrides the same path on \`mesh-refinement-study\`,
which in turn overrides \`main\`.`,
    code: `# While on mesh-refinement-study, create a sub-branch
fct.checkout("mesh-study/fine")
# parent defaults to current branch

# Or explicitly specify the parent
fct.checkout("mesh-study/coarse",
             parent="mesh-refinement-study")

# List all branches — shows the hierarchy
for b in fct.branches():
    parent = b.get("parent_branch", "—")
    print(f"  {b['name']:30s} ← {parent}")`,
    output: `[thread] Created branch 'mesh-study/fine' (from 'mesh-refinement-study')
[thread] Created branch 'mesh-study/coarse' (from 'mesh-refinement-study')

  main                           ← —
  mesh-refinement-study          ← main
  mesh-study/fine                ← mesh-refinement-study
  mesh-study/coarse              ← mesh-refinement-study`,
    diagram: "nested",
  },
  {
    id: "commits",
    title: "Commits: named checkpoints",
    prose: `A commit is a named snapshot of your branch's state. Thread computes
a SHA-256 hash of all changes since the last commit, creating an
immutable audit trail.

Every node modification is recorded in the \`nodes_history\` table by a
PostgreSQL trigger — you can't lose data even if you forget to commit.
The commit just stamps those history rows with a commit ID.`,
    code: `# Run your analysis...
import pandas as pd
forces = pd.read_csv("fine_mesh_forces.csv")
cl_max = forces["CL"].max()

fct.scalar(cl_max, name="CL_max_fine")
fct.sync()

# Commit the checkpoint
fct.commit("Fine mesh CL_max = 1.42 at alpha=12°")

# View the commit log
for c in fct.log(limit=3):
    print(f"  {c['id'][:8]}  {c['message']}")`,
    output: `[thread] Synced: 4 created, 0 updated
[thread] Commit 7a3f2e1c on 'mesh-study/fine': Fine mesh CL_max = 1.42 at alpha=12°

  7a3f2e1c  Fine mesh CL_max = 1.42 at alpha=12°
  b91c44a8  Baseline mesh convergence study
  e5d028ff  Initial geometry import`,
    diagram: "commit",
  },
  {
    id: "diff",
    title: "Diff: see what changed",
    prose: `Before merging, you want to see exactly what's different between
two branches. Thread's diff compares the **resolved** node trees —
walking the full CoW chain on each branch — and reports per-path
changes.

This works at the data level, not the file level. You see that
\`aero.CL_max\` changed from 1.38 to 1.42, not that some CSV
file was modified.`,
    code: `# Compare your fine mesh branch against main
changes = fct.diff("main")

for d in changes:
    if d["status"] != "unchanged":
        print(f"  {d['status']:10s} {d['path']}")`,
    output: `  modified   aero.CL_max
  modified   aero.CD_min
  modified   mesh.element_count
  added      aero.CL_max_fine
  added      mesh.y_plus_avg`,
    diagram: "diff",
  },
  {
    id: "merge-preview",
    title: "Merge preview: three-way comparison",
    prose: `Thread's merge uses a **three-way algorithm** — it finds the common
ancestor (merge base) of both branches, then compares what each side
changed relative to that base. This is the same algorithm git uses.

If only one side changed a path, it auto-merges. If both sides changed
the same path to different values, that's a **conflict**.`,
    code: `# Preview the merge before committing to it
preview = fct.merge_preview(target="main")

for p in preview:
    if p["conflict"]:
        print(f"  ⚠ CONFLICT  {p['path']}")
        print(f"    source: {p['source_value']}")
        print(f"    target: {p['target_value']}")
    elif p["action"] not in ("no_action", "target_only"):
        print(f"  {p['action']:10s} {p['path']}")`,
    output: `[thread] Merge preview: 5 changes, 1 conflicts

  added      aero.CL_max_fine
  added      mesh.y_plus_avg
  modified   mesh.element_count
  ⚠ CONFLICT  aero.CL_max
    source: {"value_scalar": 1.42, "kind": "scalar"}
    target: {"value_scalar": 1.39, "kind": "scalar"}`,
    diagram: "merge",
  },
  {
    id: "conflict-resolve",
    title: "Resolving conflicts: per-path choice",
    prose: `When conflicts arise, you don't have to choose "source wins everything"
or "target wins everything." Thread lets you resolve each conflict
individually — pick source for CL_max but keep target's CD_min.

This maps to how engineers actually think: "my fine mesh CL is more
accurate, but the drag from the baseline study was validated in the
wind tunnel."`,
    code: `# Resolve conflicts per-path
result = fct.merge_resolve(
    {
        "aero.CL_max": "source",   # use fine mesh value
        "aero.CD_min": "target",   # keep wind-tunnel-validated drag
    },
    into="main",
)

print(f"Merged: {result['merged_count']} changes")
print(f"Remaining conflicts: {result['conflict_count']}")`,
    output: `[thread] Resolved merge 'mesh-study/fine' → 'main': 5 merged, 0 remaining conflicts
Merged: 5 changes
Remaining conflicts: 0`,
    diagram: "conflict-resolve",
  },
  {
    id: "tags",
    title: "Tags: milestones that stick",
    prose: `Tags are named pointers to specific commits — they don't move. Use them
to mark validated baselines, design reviews, or release candidates.

Unlike branches, tags are immutable. \`v1.0-baseline\` always points to
the exact commit it was created on, even as the branch moves forward.`,
    code: `# Tag the current state as a milestone
fct.commit("Validated aero database - all runs converged")
fct.tag("v1.0-baseline",
        message="Wind-tunnel-validated CL/CD database")

# List all tags
for t in fct.tags():
    print(f"  {t['name']:20s}  {t['branch']:10s}  {t['message']}")

# Later, you can always refer back to this exact state`,
    output: `[thread] Commit a4e991bc on 'main': Validated aero database - all runs converged
[thread] Tag 'v1.0-baseline' created on 'main'

  v1.0-baseline         main        Wind-tunnel-validated CL/CD database`,
    diagram: "tags",
  },
  {
    id: "squash",
    title: "Squash: clean up before merge",
    prose: `During exploration, you might make dozens of small commits —
"trying alpha=12", "oops, wrong mesh", "actually use fine mesh".
Before merging to main, squash them into a single clean commit.

The squash operation re-links all history rows to the new commit and
preserves tags. The audit trail in \`nodes_history\` is never lost —
it's just grouped under one commit instead of many.`,
    code: `# On a messy branch with many small commits
fct.checkout("experiment-branch")
print(f"Commits before: {len(fct.log())}")

# Squash into one clean commit
fct.squash("Final: validated high-alpha CL database")
print(f"Commits after: {len(fct.log())}")

# Now merge cleanly
fct.merge(into="main")`,
    output: `[thread] Switched to branch 'experiment-branch'
Commits before: 14
[thread] Squashed commits on 'experiment-branch': 9b2c1a4f
Commits after: 1
[thread] Merged 'experiment-branch' → 'main'`,
    diagram: "squash",
  },
  {
    id: "full-workflow",
    title: "Putting it all together",
    prose: `Here's the complete workflow an aero engineer uses daily:

**1. Branch** from main for your experiment.
**2. Work** — run simulations, analyse data, make plots. Thread tracks everything automatically.
**3. Commit** checkpoints as you go.
**4. Preview** the merge to see what changed.
**5. Resolve** any conflicts with engineering judgement.
**6. Tag** validated milestones.
**7. Merge** back to main when ready.

The entire history is preserved. Every decision is traceable. Every
result is reproducible. That's what Thread gives you — git for physics.`,
    code: `import flexcomputethread as fct

# 1. Connect and branch
fct.connect(project_id="cessna-172")
fct.checkout("high-alpha-study")

# 2. Work (provenance captured automatically)
fct.auto_setup()
forces = pd.read_csv("high_alpha_forces.csv")
cl_max = fct.scalar(forces["CL"].max(), name="CL_max")

# 3. Commit
fct.sync()
fct.commit("High-alpha CL database complete")

# 4. Preview merge
preview = fct.merge_preview(target="main")

# 5. Resolve conflicts
fct.merge_resolve({"aero.CL_max": "source"}, into="main")

# 6. Tag the milestone
fct.tag("v2.0", message="High-alpha validated database")

# Done. History preserved. Decisions traceable.`,
    output: `[thread] Connected, project=cessna-172, branch=main
[thread] Created branch 'high-alpha-study' (from 'main')
[thread] Synced: 12 created
[thread] Commit f7a1b2c3: High-alpha CL database complete
[thread] Merge preview: 8 changes, 1 conflicts
[thread] Resolved merge: 8 merged, 0 remaining
[thread] Tag 'v2.0' created on 'main'`,
    diagram: "full-workflow",
  },
];

// ---------------------------------------------------------------------------
// Diagram components — metro-map style with glowing bezier curves
// ---------------------------------------------------------------------------

function BranchDiagram({ type, progress }: { type: Step["diagram"]; progress: number }) {
  const p = Math.min(1, Math.max(0, progress));
  const W = 520;
  const H = 260;

  if (type === "branch-create") {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
        <SvgDefs />
        <GridBg w={W} h={H} />

        {/* Main branch line */}
        <AnimatedPath d={`M 40 180 L ${W - 40} 180`} color={C.main} width={3} glow="glow-blue" progress={1} />

        {/* Commits on main */}
        <CommitNode cx={80} cy={180} color={C.main} glow="glow-blue" label="e5d028f" />
        <CommitNode cx={180} cy={180} color={C.main} glow="glow-blue" label="b91c44a" />
        <CommitNode cx={300} cy={180} color={C.main} glow="glow-blue" label="3fa91b2" />
        <CommitNode cx={420} cy={180} color={C.main} glow="glow-blue" label="HEAD" />

        {/* Branch label */}
        <BranchLabel x={80} y={220} text="main" color={C.main} />

        {/* Feature branch — bezier curve from fork point */}
        {p > 0.15 && (
          <AnimatedPath
            d="M 180 180 C 210 180, 220 120, 260 100 L 400 60"
            color={C.feature} width={2.5} glow="glow-orange" progress={(p - 0.15) / 0.6}
          />
        )}

        {/* Fork point ring */}
        {p > 0.1 && (
          <circle cx={180} cy={180} r={14} fill="none" stroke={C.feature} strokeWidth="1.5" opacity={0.5 * p}>
            <animate attributeName="r" from="10" to="20" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" from="0.6" to="0" dur="1.5s" repeatCount="indefinite" />
          </circle>
        )}

        {/* Commits on feature branch */}
        {p > 0.4 && <CommitNode cx={260} cy={100} color={C.feature} glow="glow-orange" label="a7c3f21" />}
        {p > 0.6 && <CommitNode cx={340} cy={75} color={C.feature} glow="glow-orange" label="d4e8b19" />}
        {p > 0.75 && <CommitNode cx={400} cy={60} color={C.feature} glow="glow-orange" pulse label="HEAD" />}

        {/* Branch label */}
        {p > 0.5 && <BranchLabel x={380} y={32} text="mesh-refinement-study" color={C.feature} />}
      </svg>
    );
  }

  if (type === "nested") {
    return (
      <svg viewBox={`0 0 ${W} 300`} style={{ width: "100%" }}>
        <SvgDefs />
        <GridBg w={W} h={300} />

        {/* Main */}
        <AnimatedPath d={`M 40 250 L ${W - 40} 250`} color={C.main} width={3} glow="glow-blue" />
        <CommitNode cx={80} cy={250} color={C.main} glow="glow-blue" />
        <CommitNode cx={160} cy={250} color={C.main} glow="glow-blue" />
        <CommitNode cx={260} cy={250} color={C.main} glow="glow-blue" />
        <BranchLabel x={80} y={282} text="main" color={C.main} />

        {/* Level 1: mesh-refinement-study */}
        {p > 0.15 && (
          <AnimatedPath d="M 160 250 C 190 250, 200 190, 240 170 L 440 130"
            color={C.feature} width={2.5} glow="glow-orange" progress={(p - 0.15) / 0.35} />
        )}
        {p > 0.3 && <CommitNode cx={240} cy={170} color={C.feature} glow="glow-orange" />}
        {p > 0.35 && <CommitNode cx={340} cy={148} color={C.feature} glow="glow-orange" />}
        {p > 0.4 && <CommitNode cx={440} cy={130} color={C.feature} glow="glow-orange" pulse />}
        {p > 0.35 && <BranchLabel x={380} y={110} text="mesh-refinement-study" color={C.feature} />}

        {/* Level 2a: mesh-study/fine */}
        {p > 0.5 && (
          <AnimatedPath d="M 340 148 C 360 148, 370 100, 400 80 L 470 55"
            color={C.fine} width={2} glow="glow-green" progress={(p - 0.5) / 0.25} />
        )}
        {p > 0.6 && <CommitNode cx={400} cy={80} color={C.fine} glow="glow-green" />}
        {p > 0.7 && <CommitNode cx={470} cy={55} color={C.fine} glow="glow-green" pulse />}
        {p > 0.65 && <BranchLabel x={450} y={30} text="mesh-study/fine" color={C.fine} />}

        {/* Level 2b: mesh-study/coarse */}
        {p > 0.7 && (
          <AnimatedPath d="M 340 148 C 350 148, 360 70, 390 40 L 420 20"
            color={C.coarse} width={2} glow="glow-violet" progress={(p - 0.7) / 0.25} dashed />
        )}
        {p > 0.85 && <CommitNode cx={420} cy={20} color={C.coarse} glow="glow-violet" pulse />}
        {p > 0.85 && <BranchLabel x={360} y={8} text="mesh-study/coarse" color={C.coarse} />}
      </svg>
    );
  }

  if (type === "commit") {
    const commits = [
      { hash: "e5d028f", msg: "Initial geometry import", x: 100 },
      { hash: "b91c44a", msg: "Baseline mesh convergence", x: 230 },
      { hash: "7a3f2e1", msg: "Fine mesh CL_max = 1.42", x: 390 },
    ];
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
        <SvgDefs />
        <GridBg w={W} h={H} />

        <AnimatedPath d={`M 40 130 L ${W - 40} 130`} color={C.feature} width={3} glow="glow-orange" />
        <BranchLabel x={100} y={30} text="mesh-study/fine" color={C.feature} />

        {commits.map((c, i) => {
          const visible = p > i * 0.3;
          if (!visible) return null;
          const isLast = i === commits.length - 1;
          return (
            <g key={i}>
              <CommitNode cx={c.x} cy={130} color={C.feature} r={isLast ? 9 : 7}
                glow="glow-orange" pulse={isLast} />
              {/* Hash label */}
              <text x={c.x} y={160} textAnchor="middle" fill={C.text} fontSize="10"
                fontFamily="'SF Mono', Menlo, monospace">{c.hash}</text>
              {/* Message card */}
              <rect x={c.x - 70} y={170} width={140} height={28} rx={6}
                fill={C.feature} opacity="0.08" stroke={C.feature} strokeWidth="0.5" />
              <text x={c.x} y={188} textAnchor="middle" fill={C.textBr} fontSize="9"
                fontFamily="'SF Mono', Menlo, monospace">{c.msg}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  if (type === "diff") {
    const items = [
      { status: "modified", path: "aero.CL_max", old: "1.38", new_: "1.42", color: C.yellow },
      { status: "modified", path: "aero.CD_min", old: "0.0231", new_: "0.0218", color: C.yellow },
      { status: "modified", path: "mesh.element_count", old: "1.2M", new_: "4.8M", color: C.yellow },
      { status: "added", path: "aero.CL_max_fine", old: "—", new_: "1.42", color: C.fine },
      { status: "added", path: "mesh.y_plus_avg", old: "—", new_: "0.82", color: C.fine },
    ];
    return (
      <svg viewBox={`0 0 ${W} 280`} style={{ width: "100%" }}>
        <SvgDefs />

        {/* Header row */}
        <text x={20} y={24} fill={C.text} fontSize="10" fontWeight="600">STATUS</text>
        <text x={110} y={24} fill={C.text} fontSize="10" fontWeight="600">PATH</text>
        <text x={330} y={24} fill={C.text} fontSize="10" fontWeight="600">main</text>
        <text x={430} y={24} fill={C.text} fontSize="10" fontWeight="600">branch</text>
        <line x1={10} y1={32} x2={W - 10} y2={32} stroke={C.grid} strokeWidth="1" />

        {items.map((item, i) => {
          const visible = p > i * 0.15;
          if (!visible) return null;
          const y = 50 + i * 46;
          return (
            <g key={i} style={{ transition: "opacity 0.4s" }}>
              {/* Row background */}
              <rect x={10} y={y - 8} width={W - 20} height={38} rx={6}
                fill={item.color} opacity="0.04" />
              {/* Status badge */}
              <rect x={14} y={y - 2} width={76} height={24} rx={12}
                fill={item.color} opacity="0.15" stroke={item.color} strokeWidth="0.5" />
              <text x={52} y={y + 12} textAnchor="middle" fill={item.color}
                fontSize="10" fontWeight="600">{item.status}</text>
              {/* Path */}
              <text x={110} y={y + 13} fill={C.textBr} fontSize="12"
                fontFamily="'SF Mono', Menlo, monospace">{item.path}</text>
              {/* Old value */}
              <text x={330} y={y + 13} fill={C.text} fontSize="11"
                fontFamily="'SF Mono', Menlo, monospace">{item.old}</text>
              {/* Arrow */}
              <text x={400} y={y + 13} fill={item.color} fontSize="14">{"→"}</text>
              {/* New value */}
              <text x={430} y={y + 13} fill={item.color} fontSize="11" fontWeight="600"
                fontFamily="'SF Mono', Menlo, monospace">{item.new_}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  if (type === "merge" || type === "conflict-resolve") {
    const resolved = type === "conflict-resolve";
    const mergeColor = resolved ? C.fine : C.main;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
        <SvgDefs />
        <GridBg w={W} h={H} />

        {/* Main branch */}
        <AnimatedPath d={`M 40 190 L ${W - 40} 190`} color={C.main} width={3} glow="glow-blue" />
        <CommitNode cx={80} cy={190} color={C.main} glow="glow-blue" label="base" />
        <CommitNode cx={200} cy={190} color={C.main} glow="glow-blue" />
        <CommitNode cx={300} cy={190} color={C.main} glow="glow-blue" label="target" />
        <BranchLabel x={80} y={230} text="main" color={C.main} />

        {/* Feature branch — bezier fork */}
        <AnimatedPath d="M 80 190 C 110 190, 120 120, 160 100 L 320 55"
          color={C.feature} width={2.5} glow="glow-orange" />
        <CommitNode cx={160} cy={100} color={C.feature} glow="glow-orange" />
        <CommitNode cx={240} cy={75} color={C.feature} glow="glow-orange" />
        <CommitNode cx={320} cy={55} color={C.feature} glow="glow-orange" label="source" />
        <BranchLabel x={260} y={30} text="feature" color={C.feature} />

        {/* Merge curve — bezier from feature tip back to main */}
        {p > 0.3 && (
          <AnimatedPath
            d="M 320 55 C 380 55, 400 140, 420 190"
            color={resolved ? C.fine : C.red}
            width={2} glow={resolved ? "glow-green" : "glow-red"}
            progress={(p - 0.3) / 0.5} dashed={!resolved}
          />
        )}

        {/* Conflict flash or resolved indicator */}
        {!resolved && p > 0.4 && p < 0.85 && (
          <g>
            <circle cx={370} cy={120} r={16} fill={C.red} opacity={0.15 + 0.1 * Math.sin(p * 20)}>
              <animate attributeName="opacity" values="0.1;0.3;0.1" dur="1s" repeatCount="indefinite" />
            </circle>
            <text x={370} y={126} textAnchor="middle" fill={C.red} fontSize="18" fontWeight="bold">!</text>
          </g>
        )}
        {resolved && p > 0.5 && (
          <g>
            <circle cx={370} cy={120} r={16} fill={C.fine} opacity="0.15" />
            <text x={370} y={126} textAnchor="middle" fill={C.fine} fontSize="16">✓</text>
          </g>
        )}

        {/* Merge commit node */}
        {p > 0.7 && (
          <g>
            <CommitNode cx={420} cy={190} color={mergeColor} r={10}
              glow={resolved ? "glow-green" : "glow-blue"} pulse />
            {/* Diamond shape for merge commit */}
            <rect x={414} y={184} width={12} height={12} rx={2}
              fill={mergeColor} transform="rotate(45 420 190)" opacity="0.3" />
          </g>
        )}
      </svg>
    );
  }

  if (type === "tags") {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
        <SvgDefs />
        <GridBg w={W} h={H} />

        {/* Main branch */}
        <AnimatedPath d={`M 40 160 L ${W - 40} 160`} color={C.main} width={3} glow="glow-blue" />
        <CommitNode cx={100} cy={160} color={C.main} glow="glow-blue" label="a1b2c3d" />
        <CommitNode cx={220} cy={160} color={C.main} glow="glow-blue" label="e4f5g6h" />
        <CommitNode cx={340} cy={160} color={C.main} glow="glow-blue" label="a4e991b" />
        <CommitNode cx={440} cy={160} color={C.main} glow="glow-blue" pulse label="HEAD" />
        <BranchLabel x={100} y={200} text="main" color={C.main} />

        {/* Tag — animated appearance */}
        {p > 0.3 && (
          <g style={{ transition: "opacity 0.5s" }}>
            {/* Tag connector line */}
            <line x1={340} y1={160} x2={340} y2={70} stroke={C.cyan} strokeWidth="1.5"
              strokeDasharray="3 3" opacity="0.6" />
            {/* Tag body — pointed shape */}
            <path d="M 290 70 L 290 46 L 384 46 L 390 58 L 384 70 Z"
              fill={C.cyan} opacity="0.12" stroke={C.cyan} strokeWidth="1" />
            {/* Tag icon circle */}
            <circle cx={300} cy={58} r={5} fill={C.cyan} opacity="0.4" />
            <circle cx={300} cy={58} r={2.5} fill={C.bg} />
            {/* Tag text */}
            <text x={315} y={63} fill={C.cyan} fontSize="13"
              fontFamily="'SF Mono', Menlo, monospace" fontWeight="700">v1.0-baseline</text>

            {/* Glow pulse on the tagged commit */}
            <circle cx={340} cy={160} r={18} fill="none" stroke={C.cyan} strokeWidth="1" opacity="0.3">
              <animate attributeName="r" from="12" to="28" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" from="0.4" to="0" dur="2s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
      </svg>
    );
  }

  if (type === "squash") {
    const beforeOpacity = p < 0.5 ? 1 : Math.max(0, 1 - (p - 0.5) * 4);
    const afterOpacity = p > 0.4 ? Math.min(1, (p - 0.4) * 4) : 0;

    return (
      <svg viewBox={`0 0 ${W} 280`} style={{ width: "100%" }}>
        <SvgDefs />
        <GridBg w={W} h={280} />

        {/* BEFORE — many small commits */}
        <g opacity={beforeOpacity} style={{ transition: "opacity 0.5s" }}>
          <text x={30} y={24} fill={C.text} fontSize="11" fontWeight="600">BEFORE</text>
          <AnimatedPath d={`M 40 70 L ${W - 40} 70`} color={C.feature} width={2.5} glow="glow-orange" />
          {[0, 1, 2, 3, 4, 5, 6].map((i) => {
            const x = 60 + i * 60;
            return <CommitNode key={i} cx={x} cy={70} color={C.feature} r={5} glow="glow-orange" />;
          })}
          <BranchLabel x={100} y={104} text="experiment" color={C.feature} />
          <text x={W - 80} y={104} fill={C.text} fontSize="9" fontFamily="monospace">7 commits</text>
        </g>

        {/* AFTER — single clean commit */}
        <g opacity={afterOpacity} style={{ transition: "opacity 0.5s" }}>
          <text x={30} y={164} fill={C.text} fontSize="11" fontWeight="600">AFTER</text>
          <AnimatedPath d={`M 40 210 L 240 210`} color={C.fine} width={3} glow="glow-green"
            progress={afterOpacity} />
          <CommitNode cx={140} cy={210} color={C.fine} r={11} glow="glow-green" pulse />
          {/* "S" for squash */}
          <text x={140} y={214} textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">S</text>
          <BranchLabel x={140} y={248} text="experiment" color={C.fine} />

          {/* Squash message card */}
          <rect x={190} y={196} width={280} height={28} rx={6}
            fill={C.fine} opacity="0.08" stroke={C.fine} strokeWidth="0.5" />
          <text x={200} y={214} fill={C.textBr} fontSize="10"
            fontFamily="'SF Mono', Menlo, monospace">
            "Final: validated high-alpha CL database"
          </text>
        </g>

        {/* Arrow between before and after */}
        {p > 0.35 && p < 0.7 && (
          <g opacity={Math.min(1, (p - 0.35) * 6)}>
            <path d="M 260 95 L 260 145" fill="none" stroke={C.text} strokeWidth="1.5" markerEnd="url(#arrow)" />
            <text x={275} y={125} fill={C.yellow} fontSize="11" fontWeight="600">squash</text>
          </g>
        )}

        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={C.text} />
          </marker>
        </defs>
      </svg>
    );
  }

  if (type === "full-workflow") {
    const stages = [
      { label: "branch", color: C.feature, x: 60 },
      { label: "work", color: C.yellow, x: 130 },
      { label: "commit", color: C.fine, x: 210 },
      { label: "preview", color: C.main, x: 295 },
      { label: "resolve", color: C.coarse, x: 380 },
      { label: "tag", color: C.cyan, x: 455 },
    ];
    return (
      <svg viewBox={`0 0 ${W} 180`} style={{ width: "100%" }}>
        <SvgDefs />
        <GridBg w={W} h={180} />

        {/* Connection line */}
        <AnimatedPath d={`M 60 90 L 455 90`} color={C.grid} width={2} progress={p} />

        {stages.map((s, i) => {
          const visible = p > i * 0.12;
          if (!visible) return null;
          const glowId = `glow-${["orange", "yellow", "green", "blue", "violet", "cyan"][i]}`;
          return (
            <g key={i}>
              {/* Connecting segment glow */}
              {i > 0 && (
                <line x1={stages[i - 1]!.x} y1={90} x2={s.x} y2={90}
                  stroke={s.color} strokeWidth={2} opacity={0.4}
                  filter={`url(#${glowId})`} />
              )}
              <CommitNode cx={s.x} cy={90} color={s.color} r={14} glow={glowId}
                pulse={i === stages.length - 1 && p > 0.9} />
              {/* Number */}
              <text x={s.x} y={94} textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
                {i + 1}
              </text>
              {/* Label */}
              <text x={s.x} y={130} textAnchor="middle" fill={s.color} fontSize="11"
                fontFamily="'SF Mono', Menlo, monospace" fontWeight="600">{s.label}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Code block with syntax highlighting
// ---------------------------------------------------------------------------

function CodeBlock({ code, output }: { code: string; output?: string }) {
  return (
    <div style={{ fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: 13 }}>
      <pre style={{
        background: "var(--gray-2)",
        border: "1px solid var(--gray-a4)",
        borderRadius: 8,
        padding: "16px 20px",
        overflow: "auto",
        margin: 0,
        lineHeight: 1.6,
        color: "var(--gray-12)",
      }}>
        {code.split("\n").map((line, i) => {
          const trimmed = line.trimStart();
          const isComment = trimmed.startsWith("#");
          const isKeyword = /^(import |from |for |if |elif |else:|def |class |return |print|with )/.test(trimmed);
          const hasString = /["'][^"']*["']/.test(line) && !isComment;
          let color = "var(--gray-12)";
          if (isComment) color = "var(--gray-8)";
          else if (isKeyword) color = C.coarse;
          else if (hasString) color = C.fine;
          return (
            <div key={i} style={{ color }}>{line || "\u00A0"}</div>
          );
        })}
      </pre>

      {output && (
        <pre style={{
          background: "var(--gray-1)",
          border: "1px solid var(--gray-a3)",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          padding: "12px 20px",
          overflow: "auto",
          margin: 0,
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--gray-10)",
        }}>
          {output}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scroll-triggered step component
// ---------------------------------------------------------------------------

function StoryStep({
  step, isActive, progress, onVisible,
}: {
  step: Step; isActive: boolean; progress: number;
  onVisible: (id: string, ratio: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]) => {
        if (entry) onVisible(step.id, entry.intersectionRatio);
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [step.id, onVisible]);

  return (
    <section
      ref={ref}
      id={step.id}
      style={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 48,
        padding: "80px 40px",
        alignItems: "start",
        opacity: isActive ? 1 : 0.25,
        transition: "opacity 0.6s ease",
      }}
    >
      <div style={{ position: "sticky", top: 80 }}>
        <h2 style={{
          fontSize: 30, fontWeight: 700, marginBottom: 16,
          color: "var(--gray-12)", lineHeight: 1.2,
          background: "linear-gradient(135deg, var(--gray-12), var(--accent-11))",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          {step.title}
        </h2>
        <div style={{
          fontSize: 16, lineHeight: 1.75, color: "var(--gray-11)", whiteSpace: "pre-line",
        }}>
          {step.prose}
        </div>
        {step.diagram && (
          <div style={{ marginTop: 28, background: `${C.bg}cc`, borderRadius: 12,
            border: "1px solid var(--gray-a3)", padding: "16px 12px", overflow: "hidden" }}>
            <BranchDiagram type={step.diagram} progress={progress} />
          </div>
        )}
      </div>

      <div style={{ position: "sticky", top: 80 }}>
        <CodeBlock code={step.code} output={step.output} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function BranchingStory() {
  const [activeStep, setActiveStep] = useState(STEPS[0]!.id);
  const [ratios, setRatios] = useState<Record<string, number>>({});

  const handleVisible = useCallback((id: string, ratio: number) => {
    setRatios((prev) => ({ ...prev, [id]: ratio }));
  }, []);

  useEffect(() => {
    let best = STEPS[0]!.id;
    let bestRatio = 0;
    for (const [id, ratio] of Object.entries(ratios)) {
      if (ratio > bestRatio) { best = id; bestRatio = ratio; }
    }
    setActiveStep(best);
  }, [ratios]);

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto",
      fontFamily: "var(--default-font-family, system-ui, -apple-system, sans-serif)" }}>

      {/* Hero */}
      <header style={{
        minHeight: "80vh", display: "flex", flexDirection: "column",
        justifyContent: "center", padding: "0 40px",
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.feature, letterSpacing: 2,
          textTransform: "uppercase", marginBottom: 12 }}>
          Thread Documentation
        </div>
        <h1 style={{
          fontSize: 56, fontWeight: 800, lineHeight: 1.05,
          background: "linear-gradient(135deg, #fff 0%, #3b82f6 50%, #22c55e 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          marginBottom: 20,
        }}>
          Git for Physics
        </h1>
        <p style={{
          fontSize: 20, lineHeight: 1.7, color: "var(--gray-11)", maxWidth: 640,
        }}>
          How Thread versions aerodynamic data — branches, merges, conflict
          resolution, and everything in between. With real Python examples
          you can run against the Thread backend.
        </p>
        <nav style={{ marginTop: 36, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {STEPS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              style={{
                padding: "6px 16px", borderRadius: 20, fontSize: 12,
                fontFamily: "'SF Mono', Menlo, monospace", fontWeight: 500,
                background: activeStep === s.id ? "var(--accent-9)" : "var(--gray-a3)",
                color: activeStep === s.id ? "white" : "var(--gray-11)",
                textDecoration: "none", transition: "all 0.25s ease",
                border: activeStep === s.id ? "1px solid var(--accent-9)" : "1px solid transparent",
              }}
            >
              {s.id}
            </a>
          ))}
        </nav>
      </header>

      {STEPS.map((step) => (
        <StoryStep
          key={step.id} step={step}
          isActive={activeStep === step.id}
          progress={ratios[step.id] ?? 0}
          onVisible={handleVisible}
        />
      ))}

      <footer style={{
        padding: "100px 40px 60px", textAlign: "center",
        color: "var(--gray-9)", fontSize: 14,
      }}>
        <p>
          Install the SDK:{" "}
          <code style={{ background: "var(--gray-a3)", padding: "4px 12px", borderRadius: 6,
            fontFamily: "'SF Mono', Menlo, monospace" }}>
            pip install flexcomputethread
          </code>
        </p>
        <p style={{ marginTop: 12 }}>
          Read the docs at <strong>thread.dev-simulation.cloud/docs</strong>
        </p>
      </footer>
    </div>
  );
}
